import React from 'react';
import { FaRegCommentDots } from 'react-icons/fa';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { MD_CAT_BLACK } from '../constants';
import { analysisBtnStyle, buildCommentHtml } from '../reportUtils';
import { tableState } from '../state';

// Posts the analysis into the linked Azure DevOps epic's discussion. Prompts for the
// epic number when it isn't already known from an earlier fetch.
export const CommentInEpicButton = ({ contentRef }: { contentRef: React.RefObject<HTMLDivElement> }) => {
  const config = useApi(configApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [status, setStatus] = React.useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [epicInput, setEpicInput] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');

  const postComment = async (epicId: string) => {
    const node = contentRef.current;
    if (!node) return;
    setStatus('busy');
    setErrorMsg('');
    try {
      const resp = await fetchApi(`${backendUrl}/api/azdo-features/workitem/${epicId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: buildCommentHtml(node) }),
      });
      if (!resp.ok) {
        let detail = '';
        try { const b = await resp.json(); detail = b?.error || ''; } catch { detail = ''; }
        console.error(`AZDO add comment failed (${resp.status}):`, detail);
        setErrorMsg(`Couldn't post the comment (error ${resp.status}).`);
        setStatus('error');
        return;
      }
      setStatus('done');
    } catch (err) {
      console.error('Failed to post epic comment:', err);
      setErrorMsg("Couldn't reach Azure DevOps to post the comment.");
      setStatus('error');
    }
  };

  const handleClick = () => {
    if (status === 'busy' || status === 'done') return;
    if (tableState.lastEpicId) {
      postComment(tableState.lastEpicId);
    } else {
      setEpicInput('');
      setErrorMsg('');
      setDialogOpen(true);
    }
  };

  const handleDialogSubmit = () => {
    const id = epicInput.trim();
    if (!/^\d+$/.test(id)) {
      setErrorMsg('Please enter a valid numeric epic/feature id.');
      return;
    }
    tableState.lastEpicId = id;
    setDialogOpen(false);
    postComment(id);
  };

  const label =
    status === 'busy' ? 'Posting…' :
    status === 'done' ? 'Comment added' :
    status === 'error' ? 'Retry comment' :
    'Comment in Epic';

  return (
    <>
      <button
        onClick={handleClick}
        disabled={status === 'busy' || status === 'done'}
        title="Post this analysis as a comment in the Azure DevOps epic"
        style={analysisBtnStyle(status === 'busy' || status === 'done')}
      >
        <FaRegCommentDots />
        {label}
      </button>

      {dialogOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '360px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', textAlign: 'left' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '16px', color: MD_CAT_BLACK }}>Comment in Epic</h3>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#555' }}>Enter the Azure DevOps epic/feature number to post this analysis to.</p>
            <input
              value={epicInput}
              onChange={(e) => setEpicInput(e.target.value)}
              placeholder="e.g. 887975"
              style={{ width: '100%', borderRadius: '4px', border: '1px solid #ccc', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
            {errorMsg && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#d32f2f' }}>{errorMsg}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => setDialogOpen(false)}
                style={{ padding: '6px 16px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDialogSubmit}
                style={{ padding: '6px 16px', borderRadius: '4px', border: 'none', background: '#FFCD11', color: '#000', fontWeight: 600, cursor: 'pointer' }}
              >
                Post comment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
